import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Dimmer,
  Dropdown,
  Form,
  Loader,
  Message,
  Modal,
} from 'semantic-ui-react'

import { creationPrintJobUrl } from 'utils/creationPrintJobPaths'
import { optionObjectsFromCollection } from 'utils/formHelpers'

import { attemptDocumentExport } from 'EditorApp/actions/doc'

import CopyToClipboardButton from 'components/CopyToClipboardButton'
import type { PageSelection } from 'components/ExportPageTree'
import ExportPageTree, {
  countPages,
  everyPage,
} from 'components/ExportPageTree'

import type { MarkupState } from 'EditorApp/types'

interface ExportLink {
  text: string
  icon: string
  href: string
}

interface Props {
  open: boolean
  onClose: () => void
  documentId: number
  printable?: boolean
  shareable?: boolean
  exportLinks: ExportLink[]
  markup: MarkupState
  fontFamily?: string
}

const ExportModal = ({
  open,
  onClose,
  documentId,
  printable,
  shareable,
  exportLinks,
  markup,
  fontFamily,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false)
  const [selectedExportHref, setSelectedExportHref] = useState(
    exportLinks[0]?.href
  )
  const [selection, setSelection] = useState<PageSelection>({})
  const [exportError, setExportError] = useState<string | null>(null)

  const artboards = useMemo(
    () =>
      markup.artboardOrder.map((artboardId) => markup.artboards[artboardId]),
    [markup]
  )

  const pageCount = countPages(everyPage(artboards))
  const selectedCount = countPages(selection)

  useEffect(() => {
    if (!open) {
      return
    }

    setSelectedExportHref(exportLinks[0]?.href)
    setSelection(everyPage(artboards))
    setExportError(null)
  }, [exportLinks, open, artboards])

  const exportOptions = optionObjectsFromCollection(exportLinks, 'href', 'text')

  const buildExportUrl = () => {
    if (!selectedExportHref) {
      return null
    }

    const exportUrl = new URL(selectedExportHref, window.location.origin)

    if (selectedCount < pageCount) {
      artboards.forEach((artboard) => {
        const pageNumbers = selection[artboard.id] || []

        if (pageNumbers.length > 0) {
          exportUrl.searchParams.set(
            `pages[${artboard.id}]`,
            pageNumbers.join(',')
          )
        }
      })
    }

    return exportUrl.toString()
  }

  const startSelectedExport = (event: React.MouseEvent) => {
    const exportUrl = buildExportUrl()
    if (!exportUrl) {
      return
    }

    event.preventDefault()
    setExportError(null)
    setIsExporting(true)

    attemptDocumentExport(exportUrl, (errorMessage?: string) => {
      setIsExporting(false)
      if (errorMessage) {
        setExportError(errorMessage)
        return
      }

      onClose()
    })
  }

  const exportDisabled = !selectedExportHref || selectedCount === 0
  const shareUrl = buildExportUrl()
  const shareDisabled = exportDisabled || !shareUrl

  return (
    <>
      <Dimmer active={isExporting} page>
        <Loader indeterminate>Preparing File</Loader>
      </Dimmer>

      <Modal
        className="ExportMenu__modal"
        size="tiny"
        open={open}
        onClose={onClose}
      >
        <Modal.Header>export</Modal.Header>
        <Modal.Content>
          <div className="ExportMenu__modal-content">
            {exportError && (
              <Message negative>
                <Message.Header>Export failed</Message.Header>
                <p>{exportError}</p>
              </Message>
            )}

            <ExportPageTree
              artboards={artboards}
              selection={selection}
              onChange={setSelection}
              fontFamily={fontFamily}
            />

            <Form className="ExportMenu__form">
              <Form.Field>
                <label>format</label>
                <Dropdown
                  name="format"
                  selection
                  options={exportOptions}
                  value={selectedExportHref}
                  onChange={(_, data) =>
                    setSelectedExportHref(data.value as string)
                  }
                />
              </Form.Field>
            </Form>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <div className="ExportMenu__modal-actions">
            <div className="ExportMenu__modal-actions-group">
              {shareable && (
                <CopyToClipboardButton
                  link={shareUrl || ''}
                  label="copy shareable link"
                  copiedLabel="copied"
                  onTimeout={onClose}
                  timeoutMs={2000}
                  basic
                  disabled={shareDisabled}
                />
              )}
              {printable && (
                <Button basic as="a" href={creationPrintJobUrl(documentId)}>
                  commercial printer
                </Button>
              )}
            </div>
            <div className="ExportMenu__modal-actions-group">
              <Button onClick={onClose}>cancel</Button>
              <Button
                primary
                disabled={exportDisabled || isExporting}
                onClick={startSelectedExport}
              >
                {pageCount > 1 ? `export (${selectedCount})` : 'export'}
              </Button>
            </div>
          </div>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default ExportModal
